@if (state$ | async; as state) {
  <section class="quality-sense-view" [id]="panelContentId" aria-label="QualitySense observations">
    <div class="quality-sense-toolbar">
      <div class="status-chip status-{{state.status}}">
        <span class="status-dot" aria-hidden="true"></span>
        <span>{{ statusLabel(state.status) }}</span>
      </div>
      <div class="scope" title="HTTP error responses and console errors">
        HTTP error responses and console errors
      </div>
      <div class="toolbar-actions">
        <button type="button" class="toolbar-action" [disabled]="state.unreadCount === 0" (click)="markAllSeen()">
          Mark all seen
        </button>
        <button type="button" class="toolbar-action close-action" aria-label="Close QualitySense" (click)="close()">
          &times;
        </button>
      </div>
    </div>

    <div class="quality-sense-content">
      @switch (state.status) {
        @case ('idle') {
          <div class="state-message">
            <strong>No browser session selected.</strong>
            <span>Start or select a browser session to monitor HTTP and console signals.</span>
          </div>
        }
        @case ('starting') {
          <div class="state-message">
            <strong>Waiting for monitoring to attach.</strong>
            <span>Signals produced before the browser session is attached may not be captured.</span>
          </div>
        }
        @case ('not-monitored') {
          <div class="state-message">
            <strong>Not monitored.</strong>
            <span>QualitySense was not started for this browser session.</span>
          </div>
        }
        @case ('load-failed') {
          <div class="state-message state-message-warning">
            <strong>{{ state.errorMessage || 'Could not load QualitySense observations.' }}</strong>
            <span>Live observations may still appear, but earlier observations might be missing.</span>
            <button type="button" class="state-action" (click)="retry()">Retry</button>
          </div>
        }
        @case ('monitoring-disconnected') {
          <div class="state-message state-message-warning">
            <strong>{{ state.errorMessage || 'QualitySense monitoring disconnected.' }}</strong>
            <span>The observations shown so far are retained.</span>
            <button type="button" class="state-action" (click)="retry()">Retry</button>
          </div>
        }
        @case ('ended') {
          @if (state.observations.length === 0) {
            <div class="state-message">
              <strong>Session ended.</strong>
              <span>No observations are available in this Workbench tab.</span>
            </div>
          }
        }
        @case ('monitoring') {
          @if (state.observations.length === 0) {
            <div class="state-message">
              <strong>No observations.</strong>
              <span>Nothing has matched the monitored signals yet. This is not a statement of overall health.</span>
            </div>
          }
        }
      }

      @if (showObservations(state.status) && state.observations.length > 0) {
        <div class="observation-list" role="list" aria-label="QualitySense observations, newest first">
          @for (observation of state.observations; track observation.findingId; let index = $index) {
            <article class="observation-row" [class.unread]="observation.unread" role="listitem">
              <button type="button"
                      class="observation-summary"
                      [attr.aria-expanded]="isObservationExpanded(state.factSessionId, observation.findingId)"
                      [attr.aria-controls]="'quality-sense-details-' + observation.findingId"
                      [attr.aria-label]="observationAccessibleLabel(observation)"
                      (click)="toggleObservation(state.factSessionId, observation)">
                <span class="unread-dot" aria-hidden="true"></span>
                <time [attr.datetime]="observation.creationTime">{{ observation.creationTime | date:'HH:mm:ss' }}</time>
                <span class="type-chip">{{ observationType(observation) }}</span>
                <span class="severity-chip severity-{{observation.severity}}">{{ observation.severity | uppercase }}</span>
                <span class="observation-title" [title]="observationTitle(observation)">{{ observationTitle(observation) }}</span>
                @if (observation.associatedArtifacts.length > 0) {
                  <span class="evidence-count">{{ observation.associatedArtifacts.length }} evidence</span>
                }
                <span class="expand-icon" aria-hidden="true">{{ isObservationExpanded(state.factSessionId, observation.findingId) ? '−' : '+' }}</span>
              </button>

              @if (isObservationExpanded(state.factSessionId, observation.findingId)) {
                <div class="observation-details" [id]="'quality-sense-details-' + observation.findingId">
                  @if (observation.detailStatus === 'loading' || observation.detailStatus === 'not-loaded') {
                    <div class="detail-state">Loading observation details…</div>
                  } @else if (observation.detailStatus === 'load-failed') {
                    <div class="detail-state detail-state-warning">
                      <span>Could not load this observation.</span>
                      <button type="button" class="state-action" (click)="retryObservation(observation)">Retry</button>
                    </div>
                  } @else {
                    @if (observation.details?.response; as response) {
                      <div class="detail-section">
                        <strong>Matched condition</strong>
                        <span>{{ httpCondition(observation) }}</span>
                      </div>
                      <dl class="detail-grid">
                        <div>
                          <dt>Status</dt>
                          <dd>{{ response.status }}{{ response.statusText ? ' ' + response.statusText : '' }}</dd>
                        </div>
                        <div>
                          <dt>Method</dt>
                          <dd>{{ response.requestMethod || 'Unknown' }}</dd>
                        </div>
                        <div>
                          <dt>Resource type</dt>
                          <dd>{{ response.resourceType || 'Unknown' }}</dd>
                        </div>
                        <div>
                          <dt>Cache</dt>
                          <dd>{{ response.fromDiskCache ? 'Disk cache' : response.fromServiceWorker ? 'Service worker' : 'Network' }}</dd>
                        </div>
                      </dl>
                      @if (response.url) {
                        <div class="detail-section">
                          <strong>URL</strong>
                          <code>{{ response.url }}</code>
                        </div>
                      }
                    }

                    @if (observation.details?.message; as message) {
                      <div class="detail-section">
                        <strong>Matched condition</strong>
                        <span>{{ consoleCondition(observation) }}</span>
                      </div>
                      <dl class="detail-grid">
                        <div>
                          <dt>Level</dt>
                          <dd>{{ message.level || 'Unknown' }}</dd>
                        </div>
                        <div>
                          <dt>Source</dt>
                          <dd>{{ message.source || 'Unknown' }}</dd>
                        </div>
                      </dl>
                      @if (message.text) {
                        <div class="detail-section">
                          <strong>Message</strong>
                          <pre>{{ message.text }}</pre>
                        </div>
                      }
                      @if (consoleLocation(observation); as location) {
                        <div class="detail-section">
                          <strong>Location</strong>
                          <code>{{ location }}</code>
                        </div>
                      }
                    }

                    <div class="detail-section evidence-section">
                      <strong>Evidence</strong>
                      <span>Finding <code>{{ observation.findingId }}</code></span>
                      @for (artifactId of observation.associatedArtifacts; track artifactId) {
                        <span>Artifact <code>{{ artifactId }}</code></span>
                      }
                    </div>
                  }
                </div>
              }
            </article>
            @if (showUnreadDividerAfter(state.observations, index)) {
              <div class="unread-divider"><span>New since you last looked</span></div>
            }
          }
        </div>
      }
    </div>

    <footer class="quality-sense-footer">
      <span>{{ state.observations.length }} observations</span>
      <span>{{ state.unreadCount }} new</span>
      <span class="footer-caveat">Capture health is not available yet</span>
    </footer>
  </section>
}
